import React, { useState, useRef, useEffect } from "react";
import { Avatar, Dropdown, ConfigProvider, theme, Switch } from "antd";
import type { MenuProps } from "antd";
import { MoonFilled, SunFilled } from "@ant-design/icons";
import Icon from "@cloudscape-design/components/icon";
import Hotspot from "@cloudscape-design/components/hotspot";
import { Button } from "../common/Button";
import { NotificationBell } from "./NotificationBell";
import { SparklesIcon } from "@amzn/wwasft-components/icons";
import { useUserAlias, useExperimentalMode } from "src/hooks";
import { useTheme } from "src/providers";
import { Mode } from "@cloudscape-design/global-styles";
import { HELP_LINKS } from "src/config/help-links";
import { HOTSPOT_IDS } from "src/tutorials";
import "./custom-navigation-actions.scss";

/** Animation duration for Ask AI button click effect (ms) - matches CSS buttonClickSpin */
const ASK_AI_CLICK_ANIMATION_MS = 800;

/** Animation duration for exit transitions (ms) - matches CSS slideOut */
const EXIT_ANIMATION_MS = 300;

interface CustomNavigationActionsProps {
  onAskAI?: () => void;
}

export const CustomNavigationActions: React.FC<CustomNavigationActionsProps> = ({ onAskAI }) => {
  const { alias } = useUserAlias();
  const { mode, setMode } = useTheme();
  const [helpDropdownOpen, setHelpDropdownOpen] = useState(false);
  const [focusedIndex, setFocusedIndex] = useState(-1);
  const [isButtonClicked, setIsButtonClicked] = useState(false);
  const helpDropdownRef = useRef<HTMLDivElement>(null);
  const { experimentalModeEnabled, setExperimentalModeEnabled, hasFeatureAccess } = useExperimentalMode();

  const toggleTheme = () => {
    setMode(mode === Mode.Light ? Mode.Dark : Mode.Light);
  };

  const handleAskAIClick = () => {
    setIsButtonClicked(true);
    setTimeout(() => setIsButtonClicked(false), ASK_AI_CLICK_ANIMATION_MS);
    onAskAI?.();
  };

  // Close help dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        helpDropdownRef.current &&
        !helpDropdownRef.current.contains(event.target as Node)
      ) {
        setHelpDropdownOpen(false);
        setFocusedIndex(-1);
      }
    };

    if (helpDropdownOpen) {
      document.addEventListener("mousedown", handleClickOutside);
      return () =>
        document.removeEventListener("mousedown", handleClickOutside);
    }
  }, [helpDropdownOpen]);

  // Keyboard navigation
  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      if (!helpDropdownOpen) return;

      switch (event.key) {
        case "Escape":
          setHelpDropdownOpen(false);
          setFocusedIndex(-1);
          break;
        case "ArrowDown":
          event.preventDefault();
          setFocusedIndex((prev) => (prev < HELP_LINKS.length - 1 ? prev + 1 : 0));
          break;
        case "ArrowUp":
          event.preventDefault();
          setFocusedIndex((prev) => (prev > 0 ? prev - 1 : HELP_LINKS.length - 1));
          break;
        case "Enter":
          event.preventDefault();
          if (focusedIndex >= 0) {
            handleHelpLinkClick(HELP_LINKS[focusedIndex].href);
          }
          break;
      }
    };

    if (helpDropdownOpen) {
      document.addEventListener("keydown", handleKeyDown);
      return () => document.removeEventListener("keydown", handleKeyDown);
    }
  }, [helpDropdownOpen, focusedIndex]);

  const handleHelpLinkClick = (href: string) => {
    window.open(href, "_blank", "noopener,noreferrer");
    setHelpDropdownOpen(false);
    setFocusedIndex(-1);
  };

  const userMenuItems: MenuProps["items"] = [];
  
  // Add experimental mode toggle if feature flag is enabled
  if (hasFeatureAccess) {
    userMenuItems.push({
      key: 'experimental-mode',
      label: (
        <div className="experimental-mode-menu-item">
          <span>Experimental Mode</span>
          <Switch
            checked={experimentalModeEnabled}
            onChange={(checked) => setExperimentalModeEnabled(checked)}
            size="small"
          />
        </div>
      ),
    });
  }
  
  const avatarLetter = alias ? alias.charAt(0).toUpperCase() : "U";

  return (
    <div className="custom-navigation-actions">
      <NotificationBell />
      <Button
        variant="secondary"
        size="medium"
        onClick={toggleTheme}
        className="theme-toggle-button"
      >
        {mode === Mode.Light ? (
          <MoonFilled style={{ fontSize: "16px", color: "white" }} />
        ) : (
          <SunFilled style={{ fontSize: "16px", color: "white" }} />
        )}
      </Button>

      <div className="help-dropdown-wrapper" ref={helpDropdownRef}>
        <Button
          variant="secondary"
          size="medium"
          onClick={() => {
            setHelpDropdownOpen(!helpDropdownOpen);
            setFocusedIndex(-1);
          }}
          className="help-button"
          aria-expanded={helpDropdownOpen}
          aria-haspopup="menu"
        >
          <span className="help-button-content">
            <Icon name="support" />
            <span className="help-button-text">Help</span>
          </span>
        </Button>
        {helpDropdownOpen && (
          <div className="help-dropdown-menu" role="menu">
            {HELP_LINKS.map((link, index) => (
              <button
                key={link.id}
                className={`help-dropdown-item ${index === focusedIndex ? "help-dropdown-item--focused" : ""}`}
                onClick={() => handleHelpLinkClick(link.href)}
                onMouseEnter={() => setFocusedIndex(index)}
                type="button"
                role="menuitem"
                tabIndex={-1}
              >
                <span className="help-dropdown-item-label">{link.label}</span>
                <Icon name="external" size="small" />
              </button>
            ))}
          </div>
        )}
      </div>

      <ConfigProvider
        theme={{
          algorithm:
            mode === Mode.Light
              ? theme.defaultAlgorithm
              : theme.darkAlgorithm,
        }}
      >
        {onAskAI && (
          <Hotspot hotspotId={HOTSPOT_IDS.ASK_AI_BUTTON}>
            {/* Native button for custom gradient/shimmer styling - Cloudscape Button doesn't support custom children */}
            <button
              type="button"
              className={`finspace-button finspace-button--primary finspace-button--medium finspace-button--gradient ask-ai-button ${isButtonClicked ? 'ask-ai-button--clicked' : ''}`}
              onClick={handleAskAIClick}
            >
              <span className="ask-ai-button__shimmer" />
              <span className="ask-ai-button__icon">
                <SparklesIcon size="sm" twinkle animated={isButtonClicked} />
              </span>
              <span>Ask AI</span>
            </button>
          </Hotspot>
        )}
        <Dropdown
          menu={{ items: userMenuItems }}
          placement="bottomRight"
          trigger={["click"]}
        >
          <div className="user-menu-trigger">
            <Avatar
              size={32}
              style={{
                backgroundColor: mode === Mode.Light ? "rgba(139, 92, 246, 0.1)" : "rgba(167, 139, 250, 0.15)",
                color: mode === Mode.Light ? "#8b5cf6" : "#c4b5fd",
                border: mode === Mode.Light ? "1px solid rgba(139, 92, 246, 0.3)" : "1px solid rgba(167, 139, 250, 0.4)",
                cursor: "pointer",
                fontWeight: 600,
              }}
            >
              {avatarLetter}
            </Avatar>
            <div className="user-info">
              <span className="user-name">
                {alias || "User"}
                {userMenuItems.length > 0 && <span style={{ marginLeft: '4px' }}><Icon name="caret-down-filled" size="small" /></span>}
              </span>
            </div>
          </div>
        </Dropdown>
      </ConfigProvider>
    </div>
  );
};
